import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import type { FloorPlanData } from '@shared/types/floorPlans';
import { todayInTimezone } from '@shared/utils/dateFormatters';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import {
  OPERATIONS_LISTINGS_FLOOR_PLAN_INACTIVE_PATH,
  OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH,
} from '../../paths';
import type { ListingWithShifts } from './apiHelpers';
import { useListings } from './useListings';
import { groupBy } from 'lodash-es';

export interface ListingsContextState {
  clearSelectedListing: () => void;
  floorPlans: FloorPlanData[];
  isLoading: boolean;
  listings: ListingWithShifts[];
  refreshFloorPlans: () => void;
  refreshListings: () => void;
  selectedDate: string;
  selectedFloorPlan: FloorPlanData;
  setSelectedFloorPlanId: Dispatch<SetStateAction<string>>;
  selectedFloorPlanTableListingIds: string[];
  selectedListing: ListingWithShifts | undefined;
  setSelectedDate: (date: string) => void;
  setSelectedFloorPlanTableListingIds: Dispatch<SetStateAction<string[]>>;
  setSelectedListingId: (listingId: string) => void;
}

export const ListingsContext = createContext<ListingsContextState | null>(null);
ListingsContext.displayName = 'ListingsContext';

export const useListingsContext = (): ListingsContextState =>
  useDefinedContext(ListingsContext);

export const ListingsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // library hooks
  const { pathname } = useLocation();

  // internal hooks
  const { timezone } = useRestaurant();
  const {
    isLoading: isListingsLoading,
    listings: fetchedListings,
    refreshListings,
  } = useListings();

  const {
    isLoading: isFloorPlanLoading,
    floorPlans,
    fetchFloorPlans,
    selectedFloorPlan,
    setSelectedFloorPlanId,
  } = useAdminFloorPlans();

  // local state
  const [selectedDate, setSelectedDate] = useState(() =>
    todayInTimezone(timezone),
  );
  const [
    selectedFloorPlanTableListingIds,
    setSelectedFloorPlanTableListingIds,
  ] = useState<string[]>([]);
  const [selectedListingId, setSelectedListingId] = useState<string>();

  useEffect(() => {
    setSelectedFloorPlanTableListingIds([]);
  }, [selectedFloorPlan]);

  const listingsGroupedByActiveState = useMemo(() => {
    const today = todayInTimezone(timezone);

    return groupBy(fetchedListings, (l) => {
      const isActive = l.shifts.some(
        (s) => s.endDate == null || s.endDate >= today,
      );
      return isActive ? 'active' : 'inactive';
    });
  }, [fetchedListings, timezone]);

  // derived state
  const listings = useMemo(() => {
    switch (pathname) {
      case OPERATIONS_LISTINGS_FLOOR_PLAN_INACTIVE_PATH:
        return listingsGroupedByActiveState.inactive ?? [];
      case OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH:
        return listingsGroupedByActiveState.active ?? [];
      default:
        return [];
    }
  }, [fetchedListings, pathname, selectedDate]);
  const selectedListing = listings.find(
    (listing) => listing.id === selectedListingId,
  );

  // handlers
  const clearSelectedListing = () => setSelectedListingId(undefined);
  const handleSetSelectedDate = (date: string) => {
    setSelectedFloorPlanTableListingIds([]);
    setSelectedDate(date);
  };

  const value = useMemo(
    (): ListingsContextState => ({
      clearSelectedListing,
      floorPlans,
      isLoading: isListingsLoading || isFloorPlanLoading,
      listings,
      refreshFloorPlans: fetchFloorPlans,
      refreshListings,
      selectedDate,
      selectedFloorPlanTableListingIds,
      selectedListing,
      setSelectedDate: handleSetSelectedDate,
      selectedFloorPlan,
      setSelectedFloorPlanId,
      setSelectedFloorPlanTableListingIds,
      setSelectedListingId,
    }),
    [
      floorPlans,
      isFloorPlanLoading,
      isListingsLoading,
      listings,
      selectedDate,
      selectedFloorPlan,
      selectedFloorPlanTableListingIds,
      selectedListing,
    ],
  );

  return (
    <ListingsContext.Provider value={value}>
      {children}
    </ListingsContext.Provider>
  );
};
